/**
 * Pure geometry for canvas containers — a dashed region that owns a set of
 * positioned occupants and can be moved, resized and tidied as a unit.
 *
 * Data-blind and dependency-free by design: no React, no Yjs, no React Flow. The
 * module that persists containers supplies plain rects and receives plain rects,
 * so every rule here is unit-testable without a document or a canvas.
 *
 * Two conventions worth stating once, because both are load-bearing:
 *
 *  - **Containment is decided by an occupant's CENTRE**, not by overlap, and the
 *    first matching container wins. An occupant straddling two containers
 *    therefore resolves deterministically rather than belonging to both.
 *  - **A container never shrinks below its contents.** `contentsFloor` is the
 *    minimum any resize must respect; without it a resize can strand an occupant
 *    outside the very rect that is meant to clamp it.
 */

export interface Rect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

export interface Point {
  readonly x: number;
  readonly y: number;
}

/** A container's identity plus its rect, as far as geometry is concerned. */
export interface GeometryContainer {
  readonly id: string;
  readonly rect: Rect;
}

export interface ContainerPadding {
  /** Inset kept between a container edge and its nearest occupant. */
  readonly pad: number;
  /** Extra top inset, reserving room for a floated header pill. */
  readonly headerPad: number;
}

export const DEFAULT_CONTAINER_PADDING: ContainerPadding = {
  pad: 24,
  headerPad: 44,
};

/** The centre of a rect — the point every containment decision is made on. */
export function centreOf(rect: Rect): Point {
  return { x: rect.x + rect.w / 2, y: rect.y + rect.h / 2 };
}

/** Whether a point falls inside a rect, edges inclusive. */
export function containsPoint(rect: Rect, point: Point): boolean {
  return (
    point.x >= rect.x &&
    point.x <= rect.x + rect.w &&
    point.y >= rect.y &&
    point.y <= rect.y + rect.h
  );
}

/**
 * The container an occupant belongs to, by centre containment, first match wins.
 * Returns `null` for an occupant over open canvas — a parked occupant, which is a
 * real state and not an error.
 */
export function containerAt(
  containers: readonly GeometryContainer[],
  occupant: Rect,
): GeometryContainer | null {
  const centre = centreOf(occupant);
  return containers.find((c) => containsPoint(c.rect, centre)) ?? null;
}

/**
 * The tightest rect enclosing every input. Returns `null` for an empty input
 * rather than a degenerate zero rect, so callers must decide what an empty
 * container means instead of silently inheriting `0,0,0,0`.
 */
export function boundingBox(rects: readonly Rect[]): Rect | null {
  const first = rects[0];
  if (!first) return null;
  let minX = first.x;
  let minY = first.y;
  let maxX = first.x + first.w;
  let maxY = first.y + first.h;
  for (const r of rects) {
    if (r.x < minX) minX = r.x;
    if (r.y < minY) minY = r.y;
    if (r.x + r.w > maxX) maxX = r.x + r.w;
    if (r.y + r.h > maxY) maxY = r.y + r.h;
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

/**
 * The smallest rect that holds `occupants` with padding, never smaller than
 * `floor`. With no occupants the floor IS the answer — which is how a container
 * that is empty by construction stays a visible, droppable target.
 *
 * Note the asymmetry: the top inset is `headerPad` so a floated header pill does
 * not overlap the first occupant.
 */
export function contentsFloor(
  occupants: readonly Rect[],
  floor: Rect,
  padding: ContainerPadding = DEFAULT_CONTAINER_PADDING,
): Rect {
  const bbox = boundingBox(occupants);
  if (!bbox) return floor;
  const x = Math.min(floor.x, bbox.x - padding.pad);
  const y = Math.min(floor.y, bbox.y - padding.headerPad);
  const right = Math.max(floor.x + floor.w, bbox.x + bbox.w + padding.pad);
  const bottom = Math.max(floor.y + floor.h, bbox.y + bbox.h + padding.pad);
  return { x, y, w: right - x, h: bottom - y };
}

/**
 * Clamp a resize so the container still holds its occupants. Only `w`/`h` are
 * constrained — the origin is a move, not a resize, and is handled by
 * `translateRect`.
 */
export function clampResize(
  next: Rect,
  occupants: readonly Rect[],
  padding: ContainerPadding = DEFAULT_CONTAINER_PADDING,
): Rect {
  const bbox = boundingBox(occupants);
  if (!bbox) return next;
  const minW = bbox.x + bbox.w + padding.pad - next.x;
  const minH = bbox.y + bbox.h + padding.pad - next.y;
  return {
    x: next.x,
    y: next.y,
    w: Math.max(next.w, minW),
    h: Math.max(next.h, minH),
  };
}

/** Move a rect by a delta. */
export function translateRect(rect: Rect, dx: number, dy: number): Rect {
  return { x: rect.x + dx, y: rect.y + dy, w: rect.w, h: rect.h };
}

/**
 * The delta that moves `from` onto `to`. Used to derive one delta from a
 * container's own move and then apply it to every occupant, so the container and
 * its contents can never disagree about how far they travelled.
 */
export function deltaBetween(from: Rect, to: Rect): Point {
  return { x: to.x - from.x, y: to.y - from.y };
}

/**
 * The inner rect an occupant of size `w`×`h` may be positioned within, expressed
 * as React Flow's `extent` corners.
 *
 * **React Flow clamps a position into `[min, max − nodeSize]`** — it subtracts the
 * node's own size from the max corner itself. So the max corner passed here must
 * be the CONTENT bound, not the position bound. Getting this backwards is what
 * once shoved every swimlane to `x = -w`.
 */
export function occupantExtent(
  container: Rect,
  padding: ContainerPadding = DEFAULT_CONTAINER_PADDING,
): [[number, number], [number, number]] {
  const minX = container.x + padding.pad;
  const minY = container.y + padding.headerPad;
  const maxX = Math.max(minX, container.x + container.w - padding.pad);
  const maxY = Math.max(minY, container.y + container.h - padding.pad);
  return [
    [minX, minY],
    [maxX, maxY],
  ];
}

export interface TidyOptions {
  /** Left/top origin of the packed block. */
  readonly originX: number;
  readonly originY: number;
  /** Gap between containers, both axes. */
  readonly gap: number;
  /** Wrap to a new row once a container would extend past this x. */
  readonly maxRight: number;
}

export const DEFAULT_TIDY: TidyOptions = {
  originX: 60,
  originY: 70,
  gap: 52,
  maxRight: 2150,
};

/**
 * Pack containers row-major, preserving the input order, and return the rect each
 * one should move to. Sizes are never changed — tidying rearranges, it does not
 * resize, so an occupant can never be stranded by a tidy.
 *
 * The first container in a row is always placed even if it alone exceeds
 * `maxRight`; wrapping it would loop forever.
 */
export function tidyContainers(
  containers: readonly GeometryContainer[],
  options: TidyOptions = DEFAULT_TIDY,
): Map<string, Rect> {
  const out = new Map<string, Rect>();
  let x = options.originX;
  let y = options.originY;
  let rowHeight = 0;
  for (const container of containers) {
    const { rect } = container;
    const isRowStart = x === options.originX;
    if (!isRowStart && x + rect.w > options.maxRight) {
      x = options.originX;
      y += rowHeight + options.gap;
      rowHeight = 0;
    }
    out.set(container.id, { x, y, w: rect.w, h: rect.h });
    x += rect.w + options.gap;
    rowHeight = Math.max(rowHeight, rect.h);
  }
  return out;
}
